/*
 * spin-viewer.js — image-based 3D spin viewer.
 * Drag horizontally to orbit (azimuth), vertically to tilt (elevation).
 * The geometry never reaches the browser; only pre-rendered frames load.
 *
 * Mount with a container element. The grid is read from the model's
 * manifest.json automatically, so usually you only need base + slug:
 *   <div data-spin
 *        data-base="https://.../bucket"
 *        data-slug="model-slug"></div>
 *
 * Optional overrides / fallbacks if no manifest is present:
 *   data-cols, data-rows, data-pattern, data-autospin="false"
 */
(function () {
  'use strict';

  function pad2(n) { return n < 10 ? '0' + n : '' + n; }

  function SpinViewer(el) {
    this.el       = el;
    this.base     = (el.getAttribute('data-base') || '').replace(/\/+$/, '');
    this.slug     = el.getAttribute('data-slug') || '';
    this.cols     = parseInt(el.getAttribute('data-cols') || '0', 10) || 0;
    this.rows     = parseInt(el.getAttribute('data-rows') || '0', 10) || 0;
    this.pattern  = el.getAttribute('data-pattern') || 'frame_{row}_{col}.webp';
    this.autoSpin = el.getAttribute('data-autospin') !== 'false';

    if (!this.base || !this.slug) return;

    this.images = {};
    this.dragging = false;
    this.accX = 0; this.accY = 0;
    this.rafAuto = null;

    this._build();
    this._observe();
  }

  SpinViewer.prototype.url = function (row, col) {
    var name = this.pattern.replace('{row}', pad2(row)).replace('{col}', pad2(col));
    return this.base + '/' + this.slug + '/' + name;
  };

  SpinViewer.prototype._build = function () {
    this.el.classList.add('spin-viewer');
    this.el.innerHTML =
      '<div class="spin-viewer__stage">' +
        '<img class="spin-viewer__img" alt="" draggable="false">' +
        '<canvas class="spin-viewer__canvas" aria-hidden="true"></canvas>' +
        '<div class="spin-viewer__loader"><span class="spin-viewer__spinner"></span></div>' +
        '<div class="spin-viewer__hint" aria-hidden="true">Drag to rotate</div>' +
      '</div>';
    this.stage  = this.el.querySelector('.spin-viewer__stage');
    this.img    = this.el.querySelector('.spin-viewer__img');
    this.canvas = this.el.querySelector('.spin-viewer__canvas');
    this.ctx    = this.canvas.getContext('2d');
    this.tint   = null;

    this.el.addEventListener('contextmenu', function (e) { e.preventDefault(); });
    this.img.addEventListener('dragstart', function (e) { e.preventDefault(); });
    this._bindPointer();
  };

  SpinViewer.prototype._observe = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) { this._start(); return; }
    var io = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) { io.disconnect(); self._start(); }
    }, { rootMargin: '300px' });
    io.observe(this.el);
  };

  // Read the model's manifest to self-configure the grid, then load.
  SpinViewer.prototype._start = function () {
    var self = this;
    fetch(this.base + '/' + this.slug + '/manifest.json', { mode: 'cors' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (m) { self._configure(m); })
      .catch(function () { self._configure(null); });
  };

  SpinViewer.prototype._configure = function (m) {
    if (m) {
      if (m.azimuth_count)   this.cols    = m.azimuth_count;
      if (m.elevation_count) this.rows    = m.elevation_count;
      if (m.frame_pattern)   this.pattern = m.frame_pattern;
    }
    if (!this.cols) this.cols = 36;
    if (!this.rows) this.rows = 1;

    var res = (m && m.resolution && m.resolution[0]) ? m.resolution[0] : 1000;
    this.canvas.width = res;
    this.canvas.height = res;

    // Per-product start view from metafields (data-start-row / data-start-col),
    // falling back to ~lower-third elevation and column 0.
    var startRow = parseInt(this.el.getAttribute('data-start-row'), 10);
    this.row = isNaN(startRow)
      ? Math.round((this.rows - 1) / 3)
      : Math.max(0, Math.min(this.rows - 1, startRow));
    var startCol = parseInt(this.el.getAttribute('data-start-col'), 10);
    this.col = isNaN(startCol) ? 0 : ((startCol % this.cols) + this.cols) % this.cols;
    this.loadedRow = 0;
    this._load();
  };

  SpinViewer.prototype._load = function () {
    var self = this, r, c;
    this._get(this.row, this.col, function () { self._render(); });
    for (c = 0; c < this.cols; c++) this._get(this.row, c);
    for (r = 0; r < this.rows; r++) {
      if (r === this.row) continue;
      for (c = 0; c < this.cols; c++) this._get(r, c);
    }
  };

  SpinViewer.prototype._get = function (row, col, cb) {
    var key = row + '_' + col, self = this;
    if (this.images[key]) { if (cb) cb(); return; }
    var img = new Image();
    img.onload = function () {
      if (row === self.row) {
        self.loadedRow++;
        if (self.loadedRow >= self.cols) self._ready();
      }
      if (cb) cb();
    };
    img.onerror = function () { if (cb) cb(); };
    img.src = this.url(row, col);
    this.images[key] = img;
  };

  SpinViewer.prototype._ready = function () {
    if (this._isReady) return;
    this._isReady = true;
    this.el.classList.add('is-ready');
    if (this.autoSpin) this._startAuto();
  };

  SpinViewer.prototype._render = function () {
    var img = this.images[this.row + '_' + this.col];
    if (!img || !img.src) return;
    if (this.tint) { this._paint(img); }
    else { this.img.src = img.src; }
  };

  // Recolor a frame by multiplying the tint over the shaded model, then
  // clipping back to the model's alpha so transparency is preserved.
  SpinViewer.prototype._paint = function (img) {
    if (!img.complete || !img.naturalWidth) { var s = this; img.addEventListener('load', function () { s._paint(img); }, { once: true }); return; }
    var c = this.canvas, ctx = this.ctx, w = c.width, h = c.height;
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    ctx.globalCompositeOperation = 'multiply';
    ctx.fillStyle = this.tint;
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(img, 0, 0, w, h);
    ctx.globalCompositeOperation = 'source-over';
  };

  // hex (e.g. "#cc2030") to tint the model, or null/falsy to clear.
  SpinViewer.prototype.setTint = function (hex) {
    this.tint = hex || null;
    if (this.tint) { this.canvas.style.display = 'block'; this.img.style.display = 'none'; }
    else { this.canvas.style.display = 'none'; this.img.style.display = ''; }
    this._render();
  };

  SpinViewer.prototype._startAuto = function () {
    var self = this, last = performance.now();
    (function step(now) {
      if (self.dragging) { self.rafAuto = null; return; }
      self.accX += (now - last) * self.cols / 6000; last = now;   // ~6s per revolution, independent of frame count
      while (self.accX >= 1) { self.col = (self.col + 1) % self.cols; self.accX -= 1; self._render(); }
      self.rafAuto = requestAnimationFrame(step);
    })(last);
  };

  SpinViewer.prototype._stopAuto = function () {
    if (this.rafAuto) { cancelAnimationFrame(this.rafAuto); this.rafAuto = null; }
    this.accX = 0;
  };

  SpinViewer.prototype._bindPointer = function () {
    var self = this, stage = this.stage;

    function pt(e) {
      if (e.touches && e.touches[0]) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      return { x: e.clientX, y: e.clientY };
    }
    function down(e) {
      if (!self.cols) return;
      self.dragging = true;
      self._stopAuto();
      self.accX = 0; self.accY = 0;
      self.el.classList.add('is-dragging');
      var p = pt(e); self.lastX = p.x; self.lastY = p.y;
      if (stage.setPointerCapture && e.pointerId != null) { try { stage.setPointerCapture(e.pointerId); } catch (x) {} }
    }
    function move(e) {
      if (!self.dragging) return;
      var p = pt(e), dx = p.x - self.lastX, dy = p.y - self.lastY;
      self.lastX = p.x; self.lastY = p.y;

      var perCol = self.el.clientWidth / self.cols;
      self.accX += dx;
      while (self.accX >= perCol) { self.col = (self.col - 1 + self.cols) % self.cols; self.accX -= perCol; }
      while (self.accX <= -perCol) { self.col = (self.col + 1) % self.cols; self.accX += perCol; }

      if (self.rows > 1) {
        var perRow = self.el.clientHeight / (self.rows * 5);   // easier vertical tilt
        self.accY += dy;
        // drag up -> higher elevation (see the top)
        while (self.accY <= -perRow && self.row < self.rows - 1) { self.row++; self.accY += perRow; }
        while (self.accY >= perRow && self.row > 0) { self.row--; self.accY -= perRow; }
        // don't let the accumulator run away at the tilt limits
        if (self.row === 0 && self.accY > 0) self.accY = 0;
        if (self.row === self.rows - 1 && self.accY < 0) self.accY = 0;
      }
      self._render();
      if (e.cancelable) e.preventDefault();
    }
    function up() { self.dragging = false; self.el.classList.remove('is-dragging'); }

    if (window.PointerEvent) {
      stage.addEventListener('pointerdown', down);
      window.addEventListener('pointermove', move, { passive: false });
      window.addEventListener('pointerup', up);
    } else {
      stage.addEventListener('mousedown', down);
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
      stage.addEventListener('touchstart', down, { passive: false });
      window.addEventListener('touchmove', move, { passive: false });
      window.addEventListener('touchend', up);
    }
  };

  function initAll() {
    var nodes = document.querySelectorAll('[data-spin]');
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i]._spinInit) { nodes[i]._spinInit = true; nodes[i]._spin = new SpinViewer(nodes[i]); }
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  // Tint every spin viewer on the page (used by the downloads colour selector).
  window.spinSetTint = function (hex) {
    var n = document.querySelectorAll('[data-spin]');
    for (var i = 0; i < n.length; i++) { if (n[i]._spin) n[i]._spin.setTint(hex); }
  };
  window.spinClearTint = function () { window.spinSetTint(null); };

  window.SpinViewer = SpinViewer;
  window.initSpinViewers = initAll;
})();
